import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import Card from '@/components/Card.vue';
import { api } from '@/main';
import { TimecardProject, TimecardReport, reportHtml, hourlyRateToCents } from './timecard';


export default /*#__PURE__*/_defineComponent({
  __name: 'TimecardPrintReport',
  props: {
    report: null,
    project: null,
    loading: { type: Boolean },
    weekStartsOn: null,
    disabled: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { report: TimecardReport | null; project?: TimecardProject; loading: boolean; weekStartsOn: string; disabled: boolean }


const frame = ref<HTMLIFrameElement | null>(null);
const container = ref<HTMLElement | null>(null);
const height = ref(200);
const ready = ref(false);
const error = ref('');
const details = ref('');
const savedDetails = ref('');
const editVersion = ref<number | null>(null);
const saving = ref(false);
const rate = ref<string | number | null>('');
const rateEdited = ref(false);
const rateError = computed(() => {
  try { hourlyRateToCents(rate.value); return ''; } catch (reason) { return reason instanceof Error ? reason.message : String(reason); }
});
function useDefaultRate() {
  rate.value = props.project?.hourlyRateCents == null ? '' : (props.project.hourlyRateCents / 100).toFixed(2);
  rateEdited.value = false;
}
watch(() => props.project?.id, useDefaultRate, { immediate: true });
watch(() => props.project?.hourlyRateCents, () => { if (!rateEdited.value) useDefaultRate(); });
const grouping = ref<'daily' | 'weekly'>('weekly');
const groupingOptions = [{ text: 'Daily', value: 'daily' }, { text: 'Weekly', value: 'weekly' }];
watch(() => props.project, (project, previous) => {
  if (project?.id !== previous?.id || details.value === savedDetails.value) {
    details.value = project?.reportDetails || '';
    savedDetails.value = details.value;
    editVersion.value = project?.version ?? null;
    error.value = '';
  }
}, { immediate: true });
async function saveDetails() {
  const project = props.project;
  if (!project?.id || saving.value || props.disabled) return;
  const submitted = details.value;
  saving.value = true;
  error.value = '';
  try {
    const saved: TimecardProject = await api.rpc('timecard', 'saveProjectReportDetails', {
      timecardHolderId: project.timecardHolderId,
      projectId: project.id,
      version: editVersion.value,
      reportDetails: submitted,
    });
    if (props.project?.id === project.id) {
      savedDetails.value = saved.reportDetails || '';
      if (details.value === submitted) details.value = savedDetails.value;
      editVersion.value = saved.version;
    }
  } catch (reason) {
    if (props.project?.id === project.id) error.value = reason instanceof Error ? reason.message : String(reason);
  } finally { saving.value = false; }
}
const html = computed(() => {
  if (!props.report || !props.project?.id || props.loading || rateError.value) return '';
  const hourlyRateCents = hourlyRateToCents(rate.value);
  if (hourlyRateCents == null) return '';
  if (props.report.days.some(day => day.projectId !== props.project?.id)) return '';
  return reportHtml(props.report, props.project, {
    details: details.value,
    hourlyRateCents,
    grouping: grouping.value,
    weekStartsOn: props.weekStartsOn,
  });
});
watch(html, () => { ready.value = false; error.value = ''; });

function resize() {
  const body = frame.value?.contentDocument?.body;
  if (body) height.value = Math.ceil(body.getBoundingClientRect().height) + 2;
}
function loaded() {
  resize();
  ready.value = true;
}
function print() {
  if (!ready.value || !html.value || !frame.value?.contentWindow) return;
  try {
    frame.value.contentWindow.focus();
    frame.value.contentWindow.print();
  } catch {
    error.value = 'Could not open the print dialog. Please try again.';
  }
}
let observer: ResizeObserver | null = null;
onMounted(() => {
  let width = 0;
  observer = new ResizeObserver(entries => {
    const newWidth = entries[0].contentRect.width;
    if (width !== newWidth) { width = newWidth; resize(); }
  });
  if (container.value) observer.observe(container.value);
});
onUnmounted(() => observer?.disconnect());

return { __sfc: true,props, frame, container, height, ready, error, details, savedDetails, editVersion, saving, rate, rateEdited, rateError, useDefaultRate, grouping, groupingOptions, saveDetails, html, resize, loaded, print, observer, Card }
}

})